import {
    ButtonGradientPrimary,
    ButtonSecondary,
    MultilineInput,
    SelectFilled,
    TextInput,
    type OptionProps,
} from '@amelio/ui-library';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
    CompetencyFormFields,
    competencySchema,
    isNameConflictError,
    NAME_MAX_LENGTH,
    DESCRIPTION_MAX_LENGTH,
    type CompetencyFormData,
} from '../validation';
import { CharacterCount } from './profiles/CharacterCount';

import type { TranslationKey } from '@/i18n-key';

import { FormField } from '@/components/FormField/FormField';
import { useAmelioTranslations } from '@/hooks';
import { validationMessageKey } from '@/utils/validationMessage';

const NAME_DUPLICATE_MESSAGE: TranslationKey = 'competencies.validation.competencyNameDuplicate';

type CompetencyFormProps = {
    mode: 'create' | 'edit';
    defaultValues: CompetencyFormData;
    groupOptions: OptionProps[];
    isSubmitting: boolean;
    onSubmit: (data: CompetencyFormData) => void;
    onCancel: () => void;
};

export const CompetencyForm = ({
    mode,
    defaultValues,
    groupOptions,
    isSubmitting,
    onSubmit,
    onCancel,
}: CompetencyFormProps) => {
    const { t } = useAmelioTranslations();

    const [nameConflict, setNameConflict] = useState(false);

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<CompetencyFormData>({
        resolver: zodResolver(competencySchema),
        defaultValues,
    });

    const handleValidSubmit = async (data: CompetencyFormData) => {
        setNameConflict(false);

        try {
            await onSubmit(data);
        } catch (error) {
            if (isNameConflictError(error)) {
                setNameConflict(true);

                return;
            }

            throw error;
        }
    };

    const nameErrorMessage = errors.name?.message
        ? t(validationMessageKey(errors.name.message))
        : nameConflict
          ? t(NAME_DUPLICATE_MESSAGE)
          : undefined;

    const resolvedGroupOptions =
        groupOptions.length > 0
            ? groupOptions
            : [
                  {
                      value: '__no-group-added__',
                      label: t('competencies.competencyForm.noGroupAdded'),
                      disabled: true,
                  },
              ];

    return (
        <form
            onSubmit={handleSubmit(handleValidSubmit)}
            className={'flex flex-col gap-4 p-1'}
        >
            <h4 className={'text-navy text-xl font-medium'}>
                {mode === 'edit'
                    ? t('competencies.competencyForm.editTitle')
                    : t('competencies.competencyForm.createTitle')}
            </h4>

            {/* Skill Title */}
            <Controller
                control={control}
                name={CompetencyFormFields.name}
                render={({ field }) => (
                    <FormField
                        label={t('competencies.competencyForm.nameLabel')}
                        labelSize={'small'}
                        required={true}
                        errorMessage={nameErrorMessage}
                    >
                        <TextInput
                            value={field.value}
                            onChange={(value) => {
                                setNameConflict(false);
                                field.onChange(value);
                            }}
                            placeholder={t('competencies.competencyForm.namePlaceholder')}
                            size={'md'}
                            radius={'md'}
                            className={
                                'focus-within:ring-primary mb-2 border border-gray-200 bg-white text-sm focus-within:border-gray-200 focus-within:ring-2 focus-within:ring-offset-2'
                            }
                            maxLength={NAME_MAX_LENGTH}
                            dataTestId={'competency-name-input'}
                        />
                        <CharacterCount
                            value={field.value}
                            maxLength={NAME_MAX_LENGTH}
                        />
                    </FormField>
                )}
            />

            {/* Description */}
            <Controller
                control={control}
                name={CompetencyFormFields.description}
                render={({ field }) => (
                    <FormField
                        label={t('competencies.competencyForm.descriptionLabel')}
                        labelSize={'small'}
                        errorMessage={
                            errors.description?.message
                                ? t(validationMessageKey(errors.description.message))
                                : undefined
                        }
                    >
                        <MultilineInput
                            value={field.value}
                            onChange={(value) => field.onChange(value)}
                            placeholder={t('competencies.competencyForm.descriptionPlaceholder')}
                            className={
                                'focus-within:ring-primary/20 border border-gray-200 bg-white text-[14px] focus-within:ring-2'
                            }
                            rows={3}
                            autoGrow={true}
                            maxLength={DESCRIPTION_MAX_LENGTH}
                            dataTestId={'competency-description-input'}
                        />
                    </FormField>
                )}
            />

            {/* Band (Group Select) */}
            <Controller
                control={control}
                name={CompetencyFormFields.competencyGroupId}
                render={({ field }) => (
                    <FormField
                        label={t('competencies.competencyForm.groupLabel')}
                        labelSize={'small'}
                        required={true}
                    >
                        <SelectFilled
                            options={resolvedGroupOptions}
                            value={field.value}
                            onChange={(value) => field.onChange(value)}
                            placeholder={t('competencies.competencyForm.groupPlaceholder')}
                            className={'border border-gray-200 bg-white text-[14px]'}
                            sort={'none'}
                            error={Boolean(errors.competencyGroupId)}
                            errorMessage={
                                errors.competencyGroupId?.message
                                    ? t(validationMessageKey(errors.competencyGroupId.message))
                                    : undefined
                            }
                            dataTestId={'competency-group-select'}
                        />
                    </FormField>
                )}
            />

            {/* Buttons Row */}
            <div className={'mt-2 flex items-center justify-end gap-3'}>
                <ButtonSecondary
                    type={'button'}
                    size={'md'}
                    onClick={onCancel}
                    dataTestId={'competency-cancel'}
                    className={'!w-[80px] !min-w-[80px] text-sm'}
                >
                    {t('common.cancel')}
                </ButtonSecondary>
                <ButtonGradientPrimary
                    type={'submit'}
                    size={'md'}
                    disabled={isSubmitting}
                    dataTestId={'competency-submit'}
                    className={'!w-[100px] !min-w-[100px] text-sm'}
                >
                    {t('common.create')}
                </ButtonGradientPrimary>
            </div>
        </form>
    );
};
